import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Smartphone,
  Activity,
  FileText,
  SlidersHorizontal,
  ClipboardList,
  Sparkles,
  LineChart,
  Check,
  TabletSmartphone,
  BarChart3,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Overline, Reveal } from "../components/common";

const PLATFORM_FEATURES = [
  {
    icon: Smartphone,
    title: "Built for outreach & mobile",
    d: "Register clients and capture information directly in the field — designed for mobile, community-based care.",
  },
  {
    icon: Activity,
    title: "Screening & clinical tracking",
    d: "Track screening progress and clinical markers for every client in one connected record.",
  },
  {
    icon: FileText,
    title: "Secure document storage",
    d: "Keep documents and records organized, secure and easy to retrieve when you need them.",
  },
  {
    icon: SlidersHorizontal,
    title: "Fully customizable",
    d: "Configure the platform to surface the business insights you need — without costly custom development.",
  },
  {
    icon: ClipboardList,
    title: "Integrated case management",
    d: "Coordinate case management and follow-up efficiently to help expedite treatment.",
  },
  {
    icon: Sparkles,
    title: "AI-powered reporting",
    d: "Automated, AI-assisted reporting means no manual spreadsheets — insights are generated for you.",
  },
  {
    icon: TabletSmartphone,
    title: "Any mobile device",
    d: "Teams can work entirely from any mobile device — no computers required, which is ideal in the field where they aren't always practical.",
  },
  {
    icon: BarChart3,
    title: "KPI dashboards",
    d: "Visual dashboards give leadership real-time insight into operations, patient care and program performance.",
  },
];

export default function Platform() {
  const { t } = useTranslation();
  return (
    <div data-testid="platform-page">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 bg-gradient-to-b from-brand-mist/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t("platform.hero.overline")}</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05] max-w-4xl lg:max-w-none lg:whitespace-nowrap">
              {t("platform.hero.titleLead")}{" "}
              <br className="hidden lg:block" />
              {t("platform.hero.titleAccent")}
            </h1>
            <div className="mt-6 max-w-2xl space-y-4">
              <p className="text-lg text-slate-500 leading-relaxed">
                {t("platform.hero.body1")}
              </p>
              <p className="text-lg text-slate-500 leading-relaxed">
                {t("platform.hero.body2")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Features + Licensing */}
      <section
        className="py-12 sm:py-16 bg-brand-offwhite"
        data-testid="platform-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal>
              <Overline className="text-brand-teal">
                {t("platform.features.overline")}
              </Overline>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold font-heading text-brand-navy leading-tight">
                {t("platform.features.titleLead")}
                <br />
                {t("platform.features.titleAccent")}
              </h2>
              <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6">
                {PLATFORM_FEATURES.map((f, i) => {
                  const loc = t("content.platformFeatures", { returnObjects: true });
                  const merged = (Array.isArray(loc) && loc[i]) ? { ...f, ...loc[i] } : f;
                  const FIcon = f.icon;
                  return (
                    <div
                      key={f.title}
                      data-testid={`platform-feature-${f.title
                        .toLowerCase()
                        .replace(/[^a-z]+/g, "-")}`}
                      className="flex gap-3"
                    >
                      <div className="h-10 w-10 shrink-0 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal">
                        <FIcon className="h-5 w-5" strokeWidth={1.7} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-brand-navy">
                          {merged.title}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500 leading-relaxed">
                          {merged.d}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Reveal>

            {/* Licensing card */}
            <Reveal delay={0.12}>
              <div
                data-testid="platform-licensing-card"
                className="lg:sticky lg:top-28 rounded-3xl bg-white p-8 sm:p-10 shadow-xl ring-1 ring-black/5"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
                  <LineChart className="h-6 w-6" strokeWidth={1.7} />
                </div>
                <h3 className="mt-5 text-xl font-bold font-heading text-brand-navy">
                  License the platform
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Put the same technology that powers HepCURE to work for your
                  organization.
                </p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-4xl font-bold font-heading text-brand-navy">
                    $10
                  </span>
                  <span className="text-slate-500">/ day per user</span>
                </div>
                <p className="mt-1 text-sm font-medium text-brand-teal">
                  Annual commitment required
                </p>

                <div className="mt-5 rounded-2xl bg-brand-mist p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-teal">
                    Flat annual rate — unlimited users
                  </p>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-slate-600">
                        Organizations
                      </span>
                      <span>
                        <span className="text-2xl font-bold font-heading text-brand-navy">
                          $10,000
                        </span>
                        <span className="text-sm text-slate-500"> / year</span>
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-slate-600">
                        Non-profits
                        <span className="block font-medium text-brand-teal">
                          (40% off)
                        </span>
                      </span>
                      <span className="shrink-0">
                        <span className="text-2xl font-bold font-heading text-brand-navy">
                          $6,000
                        </span>
                        <span className="text-sm text-slate-500"> / year</span>
                      </span>
                    </div>
                    <p
                      className="mt-3 text-[11.5px] leading-relaxed text-slate-500 italic border-t border-slate-200 pt-3"
                      data-testid="platform-nonprofit-disclaimer"
                    >
                      Non-profit rate is reserved for grassroots organizations
                      with fewer than 20 employees. Non-profits with 20+
                      employees are subject to the standard{" "}
                      <span className="whitespace-nowrap">$10,000 / year</span>{" "}
                      organization pricing.
                    </p>
                  </div>
                </div>

                <ul className="mt-6 space-y-3">
                  {[
                    "Full access to CRM platform",
                    "Client registration and screening",
                    "Comprehensive data tracking and storage",
                    "Fast track case management",
                    "Customizable insights — no costly custom development",
                    "AI-assisted, real-time reporting",
                  ].map((li) => (
                    <li
                      key={li}
                      className="flex gap-2.5 text-sm text-slate-600"
                    >
                      <Check className="h-4 w-4 shrink-0 text-brand-teal mt-0.5" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid="platform-licensing-cta"
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors"
                >
                  Enquire about licensing
                  <ArrowRight className="h-4 w-4" />
                </Link>

                <div className="mt-6 border-t border-gray-100 pt-4 space-y-2">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    AI utilization is billed separately, based on usage.
                  </p>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Prefer the platform on its own? Platform-only licensing is
                    available separately — talk to us about the option that best
                    fits your needs.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
